import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { Logo } from '../components/Logo';

/**
 * نسيتُ كلمة المرور — على نطاق الشركة، بلا جلسة.
 *
 * ثلاث خطوات: البريد ← رمزٌ يصل إليه مع كلمةٍ جديدة ← تمّ، فيدخل بها.
 * الخادم لا يقول إن كان البريد مسجّلًا، فالخطوة الثانية تظهر في كل الأحوال.
 */
export function ForgotPassword() {
  const [step, setStep] = useState<'email' | 'code' | 'done'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = (err: unknown) => {
    setError(err instanceof ApiError ? err.message : 'تعذّر الاتصال بالخادم. تحقق من الشبكة.');
    setBusy(false);
  };

  async function sendCode(e?: FormEvent) {
    e?.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await api<{ ok: true; devCode?: string }>('/auth/forgot', {
        method: 'POST',
        body: { email: email.trim() },
      });
      setDevCode(r.devCode ?? null);
      setStep('code');
      setBusy(false);
    } catch (err) {
      fail(err);
    }
  }

  async function reset(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError('كلمتا المرور غير متطابقتين.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api('/auth/forgot/confirm', {
        method: 'POST',
        body: { email: email.trim(), code: code.trim(), newPassword: password },
      });
      setStep('done');
      setBusy(false);
    } catch (err) {
      fail(err);
    }
  }

  return (
    <div className="login">
      <div className="login-card">
        <div className="login-brand">
          <Logo size={34} />
          <span>GoyOne</span>
        </div>
        <p className="login-sub">استعادة كلمة المرور</p>

        {error && (
          <div className="alert alert-danger mb-4" role="alert">
            {error}
          </div>
        )}

        {step === 'email' && (
          <form className="login-form" onSubmit={sendCode} noValidate>
            <p className="small mute" style={{ margin: 0 }}>
              اكتب بريدك المسجّل وسنرسل إليه رمزًا من ستّة أرقام.
            </p>
            <div className="field">
              <label className="label req" htmlFor="email">
                البريد الإلكتروني
              </label>
              <input
                id="email"
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoFocus
                dir="ltr"
              />
            </div>
            <button className="btn btn-primary" type="submit" disabled={busy || !email.includes('@')}>
              {busy ? 'جارٍ الإرسال' : 'أرسل الرمز'}
            </button>
            <Link to="/login" className="btn">
              رجوع إلى الدخول
            </Link>
          </form>
        )}

        {step === 'code' && (
          <form className="login-form" onSubmit={reset} noValidate>
            <p className="small mute" style={{ margin: 0 }}>
              إن كان <span className="num" dir="ltr">{email}</span> مسجّلًا في هذه الشركة فقد
              وصله رمزٌ صالحٌ لربع ساعة. لم يصل؟ افحص مجلّد الرسائل غير المرغوبة.
            </p>
            {devCode && (
              <p className="tiny" style={{ color: 'var(--warning)', margin: 0 }}>
                لأغراض التطوير — الرمز: <b className="num">{devCode}</b>
              </p>
            )}
            <div className="field">
              <label className="label req" htmlFor="code">
                الرمز
              </label>
              <input
                id="code"
                className="input num"
                dir="ltr"
                inputMode="numeric"
                maxLength={6}
                value={code}
                placeholder="••••••"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                style={{ letterSpacing: '0.3em', textAlign: 'center' }}
                autoFocus
              />
            </div>
            <div className="field">
              <label className="label req" htmlFor="new-password">
                كلمة المرور الجديدة
              </label>
              <input
                id="new-password"
                className="input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                dir="ltr"
              />
              <span className="tiny mute">عشرة أحرف على الأقل.</span>
            </div>
            <div className="field">
              <label className="label req" htmlFor="confirm-password">
                تأكيد كلمة المرور
              </label>
              <input
                id="confirm-password"
                className="input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                dir="ltr"
              />
            </div>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={busy || code.length !== 6 || password.length < 10 || !confirm}
            >
              {busy ? 'جارٍ الحفظ' : 'حفظ كلمة المرور'}
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => void sendCode()}>
              أعد إرسال الرمز
            </button>
          </form>
        )}

        {step === 'done' && (
          <div className="login-form">
            <div className="alert alert-success" role="status">
              تمّ تغيير كلمة المرور. سُجِّل خروجُك من كل الأجهزة — ادخل بالكلمة الجديدة.
            </div>
            <Link to="/login" className="btn btn-primary">
              الدخول
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
